import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Minus, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

export default function CrossInstitutionDashboard() {
  const { data: comparison, isLoading } = useQuery({
    queryKey: ["cross-institution-comparison"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get user's firm
      const { data: membership } = await supabase
        .from("firm_members" as any)
        .select("firm_id")
        .eq("user_id", user.id)
        .single();

      if (!membership) throw new Error("Not a firm member");

      // Get all institution access
      const { data: access } = await supabase
        .from("firm_institution_access" as any)
        .select("institution_id, institution_settings(institution_name)")
        .eq("firm_id", (membership as any).firm_id)
        .is("revoked_at", null);

      if (!access || access.length === 0) return [];

      // Get deals for each institution
      const institutionStats = await Promise.all(
        access.map(async (inst: any) => {
          const { data: deals } = await supabase
            .from("deals" as any)
            .select("status, amount, created_at");

          const { data: athletes } = await supabase
            .from("athletes" as any)
            .select("id");

          const totalDeals = deals?.length || 0;
          const activeDeals = deals?.filter((d: any) => d.status === "active").length || 0;
          const pendingDeals = deals?.filter((d: any) => d.status === "pending_compliance").length || 0;
          const complianceRate = totalDeals > 0 ? (activeDeals / totalDeals) * 100 : 0;

          const totalValue = deals?.reduce((sum: number, d: any) => sum + (d.amount || 0), 0) || 0;
          const avgDealValue = totalDeals > 0 ? totalValue / totalDeals : 0;

          // Calculate avg review time (mock calculation)
          const avgReviewTime = Math.floor(Math.random() * 72) + 24; // 24-96 hours

          return {
            institution: inst.institution_settings.institution_name || "Unnamed",
            institutionId: inst.institution_id,
            totalDeals,
            activeDeals,
            pendingDeals,
            totalAthletes: athletes?.length || 0,
            complianceRate,
            avgDealValue,
            totalValue,
            avgReviewTime,
          };
        })
      );

      return institutionStats;
    },
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const chartData = comparison?.map((inst) => ({
    name: inst.institution.substring(0, 15),
    deals: inst.totalDeals,
    athletes: inst.totalAthletes,
    compliance: inst.complianceRate,
  }));

  const complianceData = comparison?.map((inst) => ({
    name: inst.institution.substring(0, 20),
    value: inst.complianceRate,
  }));

  const getBestPerformer = (metric: keyof typeof comparison[0]) => {
    if (!comparison || comparison.length === 0) return null;
    return comparison.reduce((best, current) => 
      (current[metric] as number) > (best[metric] as number) ? current : best
    );
  };

  const getTrend = (current: number, avg: number) => {
    const diff = current - avg;
    if (Math.abs(diff) < 5) return <Minus className="h-4 w-4 text-muted-foreground" />;
    return diff > 0 ? 
      <TrendingUp className="h-4 w-4 text-green-600" /> : 
      <TrendingDown className="h-4 w-4 text-red-600" />;
  };

  const avgCompliance = comparison?.reduce((sum, inst) => sum + inst.complianceRate, 0) / (comparison?.length || 1);
  const avgReviewTime = comparison?.reduce((sum, inst) => sum + inst.avgReviewTime, 0) / (comparison?.length || 1);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold">Cross-Institution Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Comparative analysis across all client institutions
          </p>
        </div>

        {/* Best Performers */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Highest Compliance Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="font-semibold">{getBestPerformer("complianceRate")?.institution}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {getBestPerformer("complianceRate")?.complianceRate.toFixed(1)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Most Active Program
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="font-semibold">{getBestPerformer("totalDeals")?.institution}</p>
                  <p className="text-2xl font-bold text-primary">
                    {getBestPerformer("totalDeals")?.totalDeals} deals
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Fastest Review Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="font-semibold">{getBestPerformer("avgReviewTime")?.institution}</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {Math.min(...(comparison?.map(c => c.avgReviewTime) || [0]))}h avg
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Deals & Athletes by Institution</CardTitle>
              <CardDescription>Comparison of activity levels</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="deals" fill="#4F46E5" name="Deals" />
                  <Bar dataKey="athletes" fill="#10B981" name="Athletes" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Compliance Rate Distribution</CardTitle>
              <CardDescription>Performance across institutions</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={complianceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value.toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {complianceData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Comparison Table */}
        <Card>
          <CardHeader>
            <CardTitle>Institution Comparison Matrix</CardTitle>
            <CardDescription>Detailed metrics and benchmarks</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Institution</TableHead>
                  <TableHead className="text-right">Total Deals</TableHead>
                  <TableHead className="text-right">Active</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Athletes</TableHead>
                  <TableHead className="text-right">Compliance Rate</TableHead>
                  <TableHead className="text-right">Avg Review Time</TableHead>
                  <TableHead className="text-right">Total Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparison?.map((inst) => (
                  <TableRow key={inst.institutionId}>
                    <TableCell className="font-medium">{inst.institution}</TableCell>
                    <TableCell className="text-right">{inst.totalDeals}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {inst.activeDeals}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                        {inst.pendingDeals}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{inst.totalAthletes}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {getTrend(inst.complianceRate, avgCompliance)}
                        <span className={inst.complianceRate >= 80 ? "text-green-600 font-semibold" : ""}>
                          {inst.complianceRate.toFixed(1)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {getTrend(avgReviewTime, inst.avgReviewTime)}
                        {inst.avgReviewTime}h
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      ${inst.totalValue.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
